import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import { useQuery } from "@tanstack/react-query";

import { ACCOUNTS_API_URLS, AccountsAPI } from "../../../../api/accounts";
import { generateRandomString } from "../../../../utils/generateRandomString";
import { INewRithmicAccountData } from "../../../../api/rithmic/index.types";

import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS,
  PROVINCE_OPTIONS,
  EDX_ACCOUNT_TYPE_OPTIONS,
} from "../CreateAccountForm/constants";
import { RithmicAPI } from "../../../../api/rithmic";

interface IProps {
  selectedAccountID: number | null;
}

function updateAccountName(values: INewRithmicAccountData): string {
  const trailingType = values.eod_trailing ? "d" : "t";
  const { username, funding_location, account_type, original_account_type, counterOfAccounts } =
    values;
  const accountTypeId =
    EDX_ACCOUNT_TYPE_OPTIONS.find(({ value }) => value === account_type)?.id || "";
  return `${username}${funding_location}${accountTypeId}${original_account_type}${trailingType}${counterOfAccounts}`;
}

function splitName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  const count = parts.length;

  let firstNameParts: string[];
  let lastNameParts: string[];

  if (count === 2) {
    firstNameParts = [parts[0]];
    lastNameParts = [parts[1]];
  } else if (count === 3) {
    firstNameParts = [parts[0]];
    lastNameParts = parts.slice(1);
  } else if (count === 4) {
    firstNameParts = parts.slice(0, 2);
    lastNameParts = parts.slice(2);
  } else if (count >= 5) {
    firstNameParts = parts.slice(0, 2);
    lastNameParts = parts.slice(2);
  } else {
    // if there's only one word
    firstNameParts = [parts[0] || ""];
    lastNameParts = [];
  }

  return {
    firstName: firstNameParts.join(" "),
    lastName: lastNameParts.join(" "),
  };
}

const checkIsMatchOldConvention = (rithmicUserName: string): boolean => {
  const prefixes = ["livesim", "ivesim", "liveism", "lvesim", "livesiim", "livesiim", "liversim"];
  return prefixes.some((prefix) => rithmicUserName.toLowerCase().startsWith(prefix));
};

const FormContext: React.FC<IProps> = ({ selectedAccountID }) => {
  const { setFieldValue, values, isValid } = useFormikContext<INewRithmicAccountData>();

  useQuery({
    enabled: isValid && values.account_name !== "",
    queryKey: [
      ACCOUNTS_API_URLS.getAccountDataWithRithmicDetails,
      // values.account_type,
      // values.original_account_type,
      values.account_name,
      //values.freeAccountName,
    ],
    // enabled: isValid,
    // queryKey: [ACCOUNTS_API_URLS.getAccountDataWithRithmicDetails, values],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.append("account_name", values.account_name);
      const response = await RithmicAPI.rithmicHeliosResolveUniqueAccountName(
        query.toString(),
        signal
      );
      setFieldValue("account_name", response.data.freeAccountName);
      return response;
    },
  });

  useQuery({
    queryKey: [ACCOUNTS_API_URLS.getAccountDataWithRithmicDetails, selectedAccountID],
    queryFn: async ({ signal }) => {
      if (selectedAccountID) {
        const response = await AccountsAPI.getAccountDataWithRithmicDetails(
          selectedAccountID,
          signal
        );
        const { data } = response;

        const firstAndLastName = splitName(data.account.name);
        setFieldValue("first", firstAndLastName.firstName);
        setFieldValue("last", firstAndLastName.lastName);

        setFieldValue("address1", data.account.address);
        setFieldValue("address2", data.account.address2);
        setFieldValue("city", data.account.city);
        setFieldValue("zip", data.account.zip);

        setFieldValue("rithmic", data.rithmic);

        const country = COUNTRY_OPTIONS.find(({ value }) => value === data.account.country.name);
        if (country) setFieldValue("country", country.value);

        const COUNTRY_STATE_MAP = {
          1: STATE_OPTIONS,
          41: PROVINCE_OPTIONS,
        };

        const options =
          COUNTRY_STATE_MAP[data.account.country.id as keyof typeof COUNTRY_STATE_MAP];
        if (options) {
          const state = options.find(({ value, abbreviation }) =>
            [value, abbreviation].includes(data.account.stateprov)
          );

          setFieldValue("isDisabledState", !!state);
          if (state) setFieldValue("state", state.value);
          else setFieldValue("state", "");
        } else {
          setFieldValue("state", "");
        }

        if (data.instance === "E2T" || data.instance === "IND") {
          setFieldValue("username", `${data.rithmicE2T}f`);
          setFieldValue("existing", false);
          setFieldValue("password", generateRandomString(8));
        }

        if (data.instance === "HELIOS" && data.rithmic) {
          const isMatchOldConvention = checkIsMatchOldConvention(data.rithmic);
          if (isMatchOldConvention) {
            setFieldValue("username", `${data.rithmicE2T}f`);
            setFieldValue("existing", false);
            setFieldValue("password", generateRandomString(8));
          } else {
            setFieldValue("username", data.rithmic);
            setFieldValue("existing", true);
            setFieldValue("password", "");
          }
        }

        return response;
      }
      return null;
    },
  });

  useEffect(() => {
    const account_name = updateAccountName(values);
    setFieldValue("account_name", account_name);
  }, [
    values.username,
    values.funding_location,
    values.account_type,
    values.original_account_type,
    values.eod_trailing,
  ]);

  return null;
};

export default FormContext;
